import React, { useCallback } from 'react';
import { format } from 'date-fns';
import { ContentCard } from '../common/ContentCard';
import {
  useGetCollectionStatusQuery,
  useSetCollectionCompletedMutation,
} from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { isJobLocked } from '../../utils/lockHelpers';
import type { JobLockData } from '../../utils/types';

interface CollectionTabProps {
  jobId: string;
  lockData?: JobLockData | null;
}

export const CollectionTab: React.FC<CollectionTabProps> = ({ jobId, lockData }) => {
  const toast = useToast();
  const locked = isJobLocked(lockData);
  const { data, isLoading, refetch } = useGetCollectionStatusQuery(jobId);
  const [setCollectionCompleted, { isLoading: isSaving }] = useSetCollectionCompletedMutation();

  const handleToggle = useCallback(
    async (checked: boolean) => {
      if (locked) {
        toast.warning('Locked', 'This job is locked and collection status cannot be changed');
        return;
      }
      try {
        await setCollectionCompleted({ jobId, completed: checked }).unwrap();
        await refetch();
        toast.success(
          checked ? 'Collection completed' : 'Collection reset',
          checked ? 'Marked as collected' : 'Completion details cleared'
        );
      } catch (e: any) {
        toast.error('Update failed', e?.data?.detail || 'Could not update collection status');
      }
    },
    [jobId, locked, refetch, setCollectionCompleted, toast]
  );

  const completedAtLabel =
    data?.collection_completed_at != null
      ? format(new Date(data.collection_completed_at), 'dd/MM/yyyy HH:mm')
      : null;

  return (
    <ContentCard title="Collection">
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              checked={data?.collection_completed ?? false}
              disabled={locked || isSaving}
              onChange={(e) => handleToggle(e.target.checked)}
            />
            <span className="text-sm font-medium text-gray-900">Collection completed</span>
          </label>

          {data?.collection_completed && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm border-t border-gray-100 pt-4">
              <div>
                <dt className="text-gray-500">Completed at</dt>
                <dd className="font-medium text-gray-900">{completedAtLabel ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Completed by</dt>
                <dd className="font-medium text-gray-900">
                  {data.collection_completed_by_username ?? '—'}
                </dd>
              </div>
            </dl>
          )}
        </div>
      )}
    </ContentCard>
  );
};
